/**
 * BBR x Wave — the virtual trade walk.
 *
 * THIS FILE IS A BYTE-IDENTICAL TWIN of
 * backend/src/services/strategies/bbrWaveTrades.ts, enforced by
 * `node scripts/check-engine-parity.mjs`. Change one, change both.
 *
 * It exists for the same reason omSimpleEngine does: the marks on the chart and
 * the numbers in the study have to come from ONE implementation, or "the chart
 * agrees with the research" is a claim nobody can check. Every series it needs
 * — BBR, Wave, ATR, EMA50 — is an ARGUMENT, so this file imports nothing and
 * can be run from either side.
 *
 * ── The rule, and what it is worth ──────────────────────────────────────────
 *
 * ENTRY (long, 'BW'): BBR and Wave both at or below their oversold level, and
 * BBR rising against the previous bar. Measured over 171 Delta symbols /
 * ~102k daily bars: +2.50% over 5 days against a -0.34% baseline, 58.2% win vs
 * 44.0%, on 156 of 171 symbols, and it held out of sample. Replicated
 * independently on 33 equities over 10 years: +2.28 edge, positive in every
 * year it fired. It is NOT "BBR crosses Wave" — that measured +1.21%, and a
 * cross anywhere below zero measured -1.17%.
 *
 * EXIT: whichever comes first — BBR back to `exitLevel` (0, the midline), a
 * stop at `stopAtr` x ATR below entry, or `maxHold` bars.
 *
 * Measured THROUGH THIS FUNCTION, which matters: the first pass quoted a
 * no-stop run beside a defaulted-on stop, and verifyBwTrades caught it. Every
 * row below is at the SAME 2.5xATR stop except the two that say otherwise, and
 * every one of them is printed by that script — a number shown to a user that
 * no check recomputes is a number nobody maintains.
 *
 *                          crypto           equity (10y)
 *   midline exit  SHIPPED  +3.60% / 60.0%   +3.25% / 70.8%
 *   flat 5-bar hold        +2.20% / 59.8%   +2.39% / 64.1%
 *   wait for overbought    +1.03% / 44.9%   +3.36% / 56.9%
 *   midline, no stop       +3.90% / 60.3%   +4.39% / 77.3%
 *   tight 1.5xATR stop     +2.81% / 52.4%   +3.62% / 60.1%
 *   overbought, no stop    +1.46% / 44.9%   +5.39% / 66.4%
 *
 * A TIGHT stop is expensive: it cuts trades that go on to recover. Waiting for
 * overbought on CRYPTO is the same asymmetry as the entry — fear is a point,
 * greed is a smear, so do not wait for the top. On EQUITIES the overbought exit
 * is slightly ahead at the same stop, and clearly ahead unstopped; the midline
 * ships because it is good in BOTH and crypto is the traded market, not because
 * it wins everywhere.
 *
 * ENTRY (short, 'BD'): momentum breakdown — BBR crossing below zero, Wave below
 * zero, price below its EMA50. NOT a mirror of the long: the mirror was measured
 * dead (-0.41 edge over 10 years of equities, 95% CI [-1.15, +0.29], negative in
 * both halves) and is deliberately not implemented here. Breakdown is the best
 * of seven designs tested and is still mostly short exposure — crypto by year
 * 2024 -0.43, 2025 +1.50, 2026 -0.50. Default off.
 *
 * Nothing in this file places an order. It returns trades; the caller decides
 * whether to draw them.
 */

export interface BwBar { time: number; high: number; low: number; close: number }

export interface BwOptions {
  /** Oversold / overbought levels, per indicator. */
  bbOs: number; bbOb: number; wvOs: number; wvOb: number;
  /** Require BBR to be rising (long) / falling (short) against the previous bar. */
  requireTurn: boolean;
  /** BBR level that closes a long. 0 = the midline. */
  exitLevel: number;
  /** Long stop in ATR multiples. 0 disables it. */
  stopAtr: number;
  /** Hard bar cap on a long. */
  maxHold: number;
  shortMode: 'off' | 'breakdown';
  shortTargetAtr: number;
  shortStopAtr: number;
  shortMaxBars: number;
}

export type BwExitReason = 'level' | 'stop' | 'time';

export interface BwTrade {
  side: 'BUY' | 'SHORT';
  entryIdx: number;
  entryTime: number;
  entryPx: number;
  /** null while still open at the last evaluated bar. */
  exitIdx: number | null;
  exitTime: number | null;
  exitPx: number | null;
  why: BwExitReason | null;
  /** Signed percent in the trade's own direction; null while open. */
  retPct: number | null;
}

export const BW_DEFAULTS: BwOptions = {
  bbOs: -100, bbOb: 100, wvOs: -60, wvOb: 60,
  requireTurn: true,
  // 2.5, matching DEFAULT_CHART_SETTINGS. A tighter stop measurably costs more
  // than it saves; see the note above.
  exitLevel: 0, stopAtr: 2.5, maxHold: 20,
  shortMode: 'off', shortTargetAtr: 1.5, shortStopAtr: 1.5, shortMaxBars: 5,
};

/**
 * Walk the bars and return completed (and one possibly-open) trades.
 *
 * `lastIdx` is the last bar that may be ACTED on — callers pass the last CLOSED
 * bar, because the forming bar's BBR is still moving and a mark that appears and
 * vanishes intraday is worse than no mark.
 */
export function computeBwTrades(
  bars: BwBar[],
  bbr: Array<number | null>,
  wt1: number[],
  atr: Array<number | null>,
  ema50: Array<number | null>,
  warmup: number,
  lastIdx: number,
  opts: BwOptions,
): BwTrade[] {
  const out: BwTrade[] = [];
  const o = opts;
  const end = Math.min(lastIdx, bars.length - 1, bbr.length - 1, wt1.length - 1);
  const start = Math.max(1, warmup);
  if (end <= start) return out;

  const stopAtr = Math.max(0, o.stopAtr);
  const maxHold = Math.max(1, Math.floor(o.maxHold) || 20);
  const sTgt = Math.max(0.1, o.shortTargetAtr);
  const sStp = Math.max(0.1, o.shortStopAtr);
  const sMax = Math.max(1, Math.floor(o.shortMaxBars) || 5);

  let open: BwTrade | null = null;
  let openAtr = 0;
  // Edge-triggering: the entry condition holds for a RUN of bars, and marking
  // each one stacked arrows on adjacent candles into an unreadable smudge.
  let inLong = false, inShort = false;

  for (let i = start; i <= end; i++) {
    const b = bbr[i], bp = bbr[i - 1], w = wt1[i];
    if (b == null || bp == null || !isFinite(w)) continue;
    const px = bars[i].close;

    // Close before opening, so a single bar never does both.
    if (open) {
      const held = i - open.entryIdx;
      let why: BwExitReason | null = null;
      if (open.side === 'BUY') {
        if (stopAtr > 0 && openAtr > 0 && bars[i].low <= open.entryPx - stopAtr * openAtr) why = 'stop';
        else if (b >= o.exitLevel) why = 'level';
        else if (held >= maxHold) why = 'time';
      } else {
        if (openAtr > 0 && bars[i].high >= open.entryPx + sStp * openAtr) why = 'stop';
        else if (openAtr > 0 && bars[i].low <= open.entryPx - sTgt * openAtr) why = 'level';
        else if (held >= sMax) why = 'time';
      }
      if (why) {
        open.exitIdx = i; open.exitTime = bars[i].time; open.exitPx = px; open.why = why;
        open.retPct = (open.side === 'BUY' ? (px / open.entryPx - 1) : (open.entryPx / px - 1)) * 100;
        out.push(open);
        open = null;
      }
    }

    const longNow = b <= o.bbOs && w <= o.wvOs && (!o.requireTurn || b > bp);
    const shortNow = o.shortMode === 'breakdown'
      && bp >= 0 && b < 0 && w < 0
      && ema50[i] != null && px < (ema50[i] as number);

    if (!open && longNow && !inLong) {
      open = {
        side: 'BUY', entryIdx: i, entryTime: bars[i].time, entryPx: px,
        exitIdx: null, exitTime: null, exitPx: null, why: null, retPct: null,
      };
      openAtr = atr[i] == null ? 0 : (atr[i] as number);
    } else if (!open && shortNow && !inShort) {
      open = {
        side: 'SHORT', entryIdx: i, entryTime: bars[i].time, entryPx: px,
        exitIdx: null, exitTime: null, exitPx: null, why: null, retPct: null,
      };
      openAtr = atr[i] == null ? 0 : (atr[i] as number);
    }
    inLong = longNow; inShort = shortNow;
  }

  if (open) out.push(open);
  return out;
}
